'use client';

import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

import { FALLBACK_ERROR_MESSAGE } from '@/helpers/constants/messages';
import { isExpectedError } from '@/helpers/utils/errorHelpers';
import { reducers, slices, store } from '@/store';
import { ResponseError } from '@/types/Error';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';

type Slice = keyof typeof slices;
type Endpoint<T extends Slice> = keyof (typeof slices)[T]['endpoints'];

type Reducer = keyof typeof reducers;
type Action<T extends Reducer> = keyof (typeof reducers)[T]['actions'];

type Storable = Slice | Reducer;

type PreloadSlice<T extends Slice> = {
  endpoint: Endpoint<T>;
  data: unknown;
  args?: unknown;
  error?: FetchBaseQueryError | SerializedError;
};

type PreloadReducer<T extends Reducer> = {
  action: Action<T>;
  payload: unknown;
};

type PreloadProps<T extends Storable> = {
  slice: T /* @ts-ignore */;
} & (PreloadReducer<T> | PreloadSlice<T>);

const isReducer = (value: unknown): value is PreloadReducer<Reducer> => {
  return (value as PreloadReducer<Reducer>).action !== undefined;
};

const isSlice = (value: unknown): value is PreloadSlice<Slice> => {
  return (value as PreloadSlice<Slice>).endpoint !== undefined;
};

function Preloader<T extends Storable>(props: PreloadProps<T> | PreloadProps<T>) {
  const loaded = useRef(false);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    async function setDataToSlice() {
      if (isReducer(props)) {
        const { payload, action, slice } = props;

        if (!action || loaded.current) return;
        // @ts-ignore
        await store.dispatch(reducers[slice].actions[action](payload));
      }

      if (isSlice(props)) {
        const { endpoint, slice, data, error, args } = props;

        const responseError = error as ResponseError;
        if (responseError && !isExpectedError(responseError)) {
          const message = responseError.data?.message || responseError.message || FALLBACK_ERROR_MESSAGE;
          toast(message, { type: 'error', toastId: message });
        }

        if (!endpoint || loaded.current) return;

        // @ts-ignore
        await store.dispatch(slices[slice].util.upsertQueryData(endpoint, args || undefined, data));
      }

      loaded.current = true;
    }

    setDataToSlice();
  }, [props]);

  return <></>;
}

export default Preloader;
